import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";
import { AuthProvider, useAuth } from "./lib/auth";
import Shell from "./components/Shell";
import Login from "./pages/Login";
import Apply from "./pages/Apply";
import Pending from "./pages/Pending";
import { ForgotPassword, ResetPassword } from "./pages/PasswordReset";
import Alerts from "./pages/Alerts";
import Offers from "./pages/spec/Offers";
import MyJobs from "./pages/spec/MyJobs";
import SpecJob from "./pages/spec/SpecJob";
import Profile from "./pages/spec/Profile";
import AdminJobs from "./pages/admin/Board";
import JobForm from "./pages/admin/JobForm";
import AdminJob from "./pages/admin/AdminJob";
import Report from "./pages/admin/Report";
import Specialists from "./pages/admin/Specialists";
import SettingsPage from "./pages/admin/Settings";

function Guard({ role, children }) {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
        <Loader2 className="w-6 h-6 animate-spin text-[#FF6B00]" />
      </div>
    );
  if (!user) return <Navigate to={role === "admin" ? "/admin/login" : "/login"} replace />;
  if (role === "specialist" && user.role === "specialist" && user.status !== "approved") return <Pending />;
  if (role && user.role !== role) return <Navigate to="/" replace />;
  return children;
}

function Home() {
  const { user } = useAuth();
  if (user === null) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === "admin" ? "/a/jobs" : "/s/offers"} replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/admin/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/apply" element={<Apply />} />
          <Route element={<Guard role="specialist"><Shell /></Guard>}>
            <Route path="/s/offers" element={<Offers />} />
            <Route path="/s/jobs" element={<MyJobs />} />
            <Route path="/s/jobs/:id" element={<SpecJob />} />
            <Route path="/s/alerts" element={<Alerts />} />
            <Route path="/s/profile" element={<Profile />} />
          </Route>
          <Route element={<Guard role="admin"><Shell /></Guard>}>
            <Route path="/a/jobs" element={<AdminJobs />} />
            <Route path="/a/jobs/new" element={<JobForm />} />
            <Route path="/a/jobs/:id/edit" element={<JobForm />} />
            <Route path="/a/jobs/:id" element={<AdminJob />} />
            <Route path="/a/specialists" element={<Specialists />} />
            <Route path="/a/alerts" element={<Alerts />} />
            <Route path="/a/settings" element={<SettingsPage />} />
          </Route>
          <Route path="/a/jobs/:id/report" element={<Guard role="admin"><Report /></Guard>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster theme="light" position="top-center" richColors />
    </AuthProvider>
  );
}
